import React, { useEffect, useState } from 'react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress, List, ListItem, ListItemText, Typography } from '@mui/material';
import { fetchEditVersions, type EditVersion } from '../../services/editingApi';
import { formatMs } from './editStudioModel';

interface VersionsDialogProps {
  open: boolean;
  editId: string;
  onClose: () => void;
  /** Puts a restore point back (the studio saves it as a new version). */
  onRestore: (version: EditVersion) => Promise<void>;
}

const REASON_LABELS: Record<EditVersion['reason'], string> = {
  autosave: 'While editing',
  assistant: 'Before the assistant',
  export: 'Exported',
  restore: 'Before a restore',
  reset: 'Before starting over',
  manual: 'Saved by you',
};

/** Restore points: before the assistant changed it, at each export, and every few minutes of editing. */
const VersionsDialog: React.FC<VersionsDialogProps> = ({ open, editId, onClose, onRestore }) => {
  const [versions, setVersions] = useState<EditVersion[] | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [restoring, setRestoring] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setVersions(null);
    setProblem(null);
    fetchEditVersions(editId)
      .then((list) => { if (!cancelled) setVersions(list); })
      .catch((error) => { if (!cancelled) { setVersions([]); setProblem(error instanceof Error ? error.message : 'The versions couldn’t be loaded.'); } });
    return () => { cancelled = true; };
  }, [editId, open]);

  const restore = async (version: EditVersion) => {
    setRestoring(version.id);
    setProblem(null);
    try {
      await onRestore(version);
      onClose();
    } catch (error) {
      setProblem(error instanceof Error ? error.message : 'That version couldn’t be restored.');
    } finally {
      setRestoring(null);
    }
  };

  return (
    <Dialog open={open} onClose={restoring ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Earlier versions</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Kept before the assistant changes your edit, at every export, and every few minutes while you work. Restoring keeps today’s version too, so you can come back.
        </Typography>
        {problem ? <Alert severity="error" sx={{ mb: 1.5 }}>{problem}</Alert> : null}
        {versions === null ? <LinearProgress /> : versions.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No earlier versions yet. They appear as you edit.</Typography>
        ) : (
          <List dense disablePadding data-testid="versions-list">
            {versions.map((version) => (
              <ListItem key={version.id} disableGutters secondaryAction={<Button size="small" variant="outlined" disabled={Boolean(restoring)} onClick={() => restore(version)}>{restoring === version.id ? 'Restoring…' : 'Restore'}</Button>} sx={{ pr: 12 }}>
                <ListItemText
                  primary={`${new Date(version.createdAt).toLocaleString()} · ${version.label || REASON_LABELS[version.reason]}`}
                  secondary={`${version.summary} · ${formatMs(version.outputDurationMs)} · version ${version.version}`}
                  primaryTypographyProps={{ variant: 'body2' }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={Boolean(restoring)}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default VersionsDialog;
